import { useEffect, useMemo, useRef, useState } from 'react';
import { devicesTopic, type DeviceSummary } from '@simpleacs/shared';
import { realtime, type ConnectionState } from './ws';

export interface LiveDevices {
  devices: DeviceSummary[];
  byId: Map<string, DeviceSummary>;
  connection: ConnectionState;
  /** False until the first snapshot lands. */
  ready: boolean;
  lastUpdateAt: number | null;
}

/**
 * Live ONU table backed by the WebSocket stream: the server sends a snapshot on
 * subscribe and patches thereafter, so no polling loop lives in the browser.
 */
export function useLiveDevices(orgId: string | null): LiveDevices {
  const topic = devicesTopic(orgId);
  const [byId, setById] = useState<Map<string, DeviceSummary>>(new Map());
  const [connection, setConnection] = useState<ConnectionState>(realtime.getState());
  const [ready, setReady] = useState(false);
  const [lastUpdateAt, setLastUpdateAt] = useState<number | null>(null);
  const topicRef = useRef(topic);

  useEffect(() => {
    topicRef.current = topic;
    setReady(false);
    setById(new Map());

    const offState = realtime.onState(setConnection);
    const offMessage = realtime.onMessage((message) => {
      if (!('topic' in message) || message.topic !== topicRef.current) return;

      if (message.type === 'devices.snapshot') {
        setById(new Map(message.devices.map((d) => [d.id, d] as const)));
        setReady(true);
        setLastUpdateAt(message.at);
        return;
      }

      if (message.type === 'devices.patch') {
        setById((previous) => {
          const next = new Map(previous);
          for (const device of message.upserted) next.set(device.id, device);
          for (const id of message.removed) next.delete(id);
          return next;
        });
        setLastUpdateAt(message.at);
      }
    });

    realtime.subscribe([topic]);

    return () => {
      realtime.unsubscribe([topic]);
      offMessage();
      offState();
    };
  }, [topic]);

  const devices = useMemo(() => [...byId.values()], [byId]);

  return { devices, byId, connection, ready, lastUpdateAt };
}
